import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import Login from './pages/Login';
import Users from './pages/Users';
import MergeReview from './pages/membership/MergeReview';
import ManageUser from './pages/ManageUser';
import Devices from './pages/Devices';
import MyMembership from './pages/MyMembership';
import SquareIntegration from './pages/SquareIntegration';
import SquareCatalogConfig from './pages/SquareCatalogConfig';
import SquareSyncHistory from './pages/SquareSyncHistory';
import VerifyMember from './pages/VerifyMember';
import ApplyMembership from './pages/ApplyMembership';
import ClaimAccount from './pages/ClaimAccount';
import AcknowledgeRules from './pages/AcknowledgeRules';
import RulesCompliance from './pages/RulesCompliance';
import BoardGameLibrary from './pages/BoardGameLibrary';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DeveloperTools from './pages/DeveloperTools';
import DesignSystemReference from './pages/DesignSystemReference';
import EventsHub from './pages/EventsHub';
import EventDetails from './pages/EventDetails';
import EventManage from './pages/EventManage';
import PinGolfCard from './pages/PinGolfCard';
import PinGolfEvents from './pages/PinGolfEvents';
import PinGolfLeagueOverview from './pages/PinGolfLeagueOverview';
import PingolfHostControls from './pages/PingolfHostControls';
import EventRSVP from './pages/EventRSVP';
import RSVPConfirmation from './pages/RSVPConfirmation';
import KnowledgeBaseHome from './pages/KnowledgeBaseHome';
import KnowledgeBaseCategory from './pages/KnowledgeBaseCategory';
import KnowledgeBaseArticle from './pages/KnowledgeBaseArticle';
import CompetitionAdmin from './pages/CompetitionAdmin';
import CompetitionBroadcast from './pages/CompetitionBroadcast';
import TransformersTvDashboard from './pages/TransformersTvDashboard';
import MyCompetition from './pages/MyCompetition';
import EventSeriesManagement from './pages/EventSeriesManagement';
import SeriesDates from './pages/SeriesDates';
import PinGolfLeagueAdmin from './pages/PinGolfLeagueAdmin';
import PinGolfLeagueAdminSeason from './pages/PinGolfLeagueAdminSeason';
import PinGolfLeagueSeasonCreate from './pages/PinGolfLeagueSeasonCreate';
import PinGolfLeagueTv from './pages/PinGolfLeagueTv';
import PinGolfLeagueSeason from './pages/PinGolfLeagueSeason';
import HostVolunteerRequests from './pages/HostVolunteerRequests';
import AutomationSettings from './pages/AutomationSettings';
import MachineManagement from './pages/MachineManagement';
import MachineIssues from './pages/MachineIssues';
import MachineIssueDetail from './pages/MachineIssueDetail';
import MachineDetail from './pages/MachineDetail';
import MachinePublic from './pages/MachinePublic';
import MachineMaintenanceList from './pages/MachineMaintenanceList';
import MaintenanceRecordDetail from './pages/MaintenanceRecordDetail';
import MaintenanceTemplateEditor from './pages/MaintenanceTemplateEditor';
import BadgeManagement from './pages/BadgeManagement';
import ScorekeepingTv from './pages/ScorekeepingTv';
import EventsTv from './pages/EventsTv';
import HallwayTv from './pages/HallwayTv';
import MachineScorePage from './pages/scorekeeping/MachineScorePage';
import SubmitScore from './pages/scorekeeping/SubmitScore';
import ScoreSignup from './pages/scorekeeping/ScoreSignup';
import ScorekeepingAdmin from './pages/scorekeeping/ScorekeepingAdmin';
import Leaderboard from './pages/scorekeeping/Leaderboard';
import AccessDenied from './pages/AccessDenied';
import NotFound from './pages/NotFound';
import Navigation from './components/Navigation';
import ScanNotification from './components/ScanNotification';
import UpdateNotification from './components/UpdateNotification';
import ConnectionBanner from './components/ConnectionBanner';
import RoleGuard from './components/RoleGuard';
import FeatureRoleGuard from './components/FeatureRoleGuard';
import { ToastProvider, Toast } from './components/Toast';

import TaskList from './pages/TaskList';
import TaskDetail from './pages/TaskDetail';
import TaskSuggest from './pages/TaskSuggest';
import MySuggestions from './pages/MySuggestions';
import TaskForm from './pages/TaskForm';
import TaskTemplateLibrary from './pages/TaskTemplateLibrary';
import MaintenanceWalkthrough from './pages/MaintenanceWalkthrough';
import TaskNeedsAttention from './pages/TaskNeedsAttention';
import TaskSuggestionReview from './pages/TaskSuggestionReview';
import TaskBountyConfirm from './pages/TaskBountyConfirm';
import TaskForceUndo from './pages/TaskForceUndo';
import TaskLeaderboard from './pages/TaskLeaderboard';
import TaskReporting from './pages/TaskReporting';

import { authService } from './services/authService';
import { apiService } from './services/apiService';
import { websocketService } from './services/websocketService';
import { checkConnection, getConnectionStatus } from './services/connectionStatus';

// Renders nothing - just reports the current pathname up to App() so it can decide whether
// to render the normal app chrome (nav, toasts) or a bare standalone page (e.g. the public
// TV dashboard, which must never show the side nav even if the viewer happens to be logged in).
function LocationWatcher({ onChange }) {
  const location = useLocation();
  useEffect(() => {
    onChange(location.pathname);
  }, [location.pathname, onChange]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  // Set when the club rules have been updated since this member last signed
  // them. Sourced from the login response and refreshed with the profile, so it
  // is known before the first render and nobody sees the dashboard flash before
  // being redirected. Defaults to false: if the check is unavailable, people
  // keep using the app rather than being locked out of it.
  const [requireRulesAcknowledgment, setRequireRulesAcknowledgment] = useState(false);
  const [scanNotification, setScanNotification] = useState(null);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [waitingServiceWorker, setWaitingServiceWorker] = useState(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // The public TV dashboard must render as a bare, standalone page - no side nav, no chrome -
  // even if the person viewing it happens to be logged in on that browser.
  const isStandaloneRoute = /^\/competitions\/[^/]+\/tv\/?$/.test(currentPath)
    || /^\/pingolf-season\/[^/]+\/tv\/?$/.test(currentPath)
    // The scorekeeping club screen is the same case: it runs on a TV in the
    // club, and an admin who opens it on their own signed-in browser should see
    // what the TV shows, not a sidebar over the top of it.
    || /^\/scorekeeping\/tv\/?$/.test(currentPath)
    // The events screen, likewise a club TV.
    || /^\/events\/tv\/?$/.test(currentPath)
    // And the hallway sign by the front door.
    || /^\/hallway\/tv\/?$/.test(currentPath);

  // Handle scan result from WebSocket
  const handleScanResult = useCallback((data) => {
    setScanNotification(data);
  }, []);

  // Dismiss scan notification
  const dismissScanNotification = useCallback(() => {
    setScanNotification(null);
  }, []);

  // Helper function to connect WebSocket with current token
  const connectWebSocket = useCallback(() => {
    const token = authService.getToken();
    if (token) {
      websocketService.connect(token);
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const token = authService.getToken();
    if (token) {
      setIsAuthenticated(true);
      const user = authService.getCurrentUser();
      if (user && user.requirePasswordChange) {
        setRequirePasswordChange(true);
      }
      if (user && user.documentAcknowledgment && user.documentAcknowledgment.required) {
        setRequireRulesAcknowledgment(true);
      }
      // Connect to WebSocket when authenticated
      connectWebSocket();
      // Refresh the cached profile (roles, feature roles, tech tier, etc. can
      // change server-side between sessions) so a page refresh picks up
      // grants/demotions made after the last login instead of requiring a
      // re-login. login() stores camelCase fields (techTier, ...) while this
      // profile endpoint returns snake_case (tech_tier, ...) - most pages read
      // `techTier ?? tech_tier`, so the camelCase mirrors must be overwritten
      // too or a stale login-time value would keep winning over the refresh.
      apiService.getCurrentUser()
        .then((res) => {
          const profile = res.data;
          authService.setCurrentUser({
            ...authService.getCurrentUser(),
            ...profile,
            techTier: profile.tech_tier,
            canManageMachineIssues: profile.can_manage_machine_issues,
            notifyOnGameDown: profile.notify_on_game_down,
            qrCode: profile.qr_code,
            firstName: profile.first_name,
            lastName: profile.last_name,
            sternUsername: profile.stern_username
          });
          // Picked up on refresh as well as at login, so an admin requiring
          // re-acknowledgment reaches people already signed in rather than
          // waiting for their next login.
          setRequireRulesAcknowledgment(Boolean(profile.documentAcknowledgment?.required));
        })
        .catch(() => {});
    }
    setIsLoading(false);

    // Listen for logout events (e.g., when token expires)
    const unsubscribeLogout = authService.onLogout(() => {
      // Disconnect WebSocket
      websocketService.disconnect();
      // Update authentication state
      setIsAuthenticated(false);
      setRequirePasswordChange(false);
      setRequireRulesAcknowledgment(false);
    });

    // Cleanup listener on unmount
    return () => {
      unsubscribeLogout();
    };
  }, [connectWebSocket]);

  // A dropped socket is often the first sign the server has gone. Ask it
  // directly after a moment rather than trusting the socket, which also drops
  // for ordinary reasons and reconnects on its own. A socket coming back means
  // the server is reachable again, so check at once if the banner is showing.
  useEffect(() => {
    const unsubscribeDisconnected = websocketService.on('disconnected', ({ code } = {}) => {
      if (code !== 1000 && code !== 4001 && code !== 4003) {
        checkConnection(5000);
      }
    });
    const unsubscribeConnected = websocketService.on('connected', () => {
      if (!getConnectionStatus().reachable) {
        checkConnection();
      }
    });
    return () => {
      unsubscribeDisconnected();
      unsubscribeConnected();
    };
  }, []);

  // Set up WebSocket listener for scan results
  useEffect(() => {
    const unsubscribe = websocketService.on('scan_result', handleScanResult);
    return () => unsubscribe();
  }, [handleScanResult]);

  const handleLogin = useCallback(() => {
    setIsAuthenticated(true);
    const user = authService.getCurrentUser();
    if (user && user.requirePasswordChange) {
      setRequirePasswordChange(true);
    }
    setRequireRulesAcknowledgment(Boolean(user?.documentAcknowledgment?.required));
    // Connect to WebSocket after login
    connectWebSocket();
  }, [connectWebSocket]);

  // The login screen reports a sign-in through its onLogin prop, but it is not
  // the only place a session begins. Creating an account after posting a score
  // at a machine signs somebody in just as completely, and without this the
  // shell never found out: no header, no menu, no bell, and protected routes
  // still bouncing them to the login screen until they reloaded.
  useEffect(() => authService.onLogin(handleLogin), [handleLogin]);

  const handleLogout = () => {
    // Disconnect WebSocket before logout
    websocketService.disconnect();
    authService.logout();
    setIsAuthenticated(false);
    setRequirePasswordChange(false);
    setRequireRulesAcknowledgment(false);
  };

  const handlePasswordChanged = () => {
    setRequirePasswordChange(false);
  };

  const handleRulesAcknowledged = () => {
    setRequireRulesAcknowledgment(false);
  };

  const getServiceWorkerVersion = useCallback((worker) => {
    return new Promise((resolve) => {
      if (!worker) {
        resolve(null);
        return;
      }

      const messageChannel = new MessageChannel();
      const timeoutId = setTimeout(() => resolve(null), 1500);

      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeoutId);
        resolve(event.data && event.data.version ? event.data.version : null);
      };

      try {
        worker.postMessage({ type: 'GET_VERSION' }, [messageChannel.port2]);
      } catch (err) {
        clearTimeout(timeoutId);
        resolve(null);
      }
    });
  }, []);

  // Handle PWA update notification
  const handleUpdateAvailable = useCallback(async (registration) => {
    const waitingWorker = registration && registration.waiting;
    if (!waitingWorker) {
      return;
    }

    const activeWorker = registration.active || navigator.serviceWorker.controller;
    const [waitingVersion, activeVersion] = await Promise.all([
      getServiceWorkerVersion(waitingWorker),
      getServiceWorkerVersion(activeWorker)
    ]);

    if (waitingVersion && activeVersion && waitingVersion === activeVersion) {
      return;
    }

    setWaitingServiceWorker(waitingWorker);
    setShowUpdateNotification(true);
  }, [getServiceWorkerVersion]);

  // Update the PWA to the new version
  const handleUpdateNow = useCallback(() => {
    if (waitingServiceWorker) {
      // Send message to service worker to skip waiting
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
      
      // Listen for the controlling service worker to change (once)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Reload the page to load the new version
        window.location.reload();
      }, { once: true });
    }
    setShowUpdateNotification(false);
  }, [waitingServiceWorker]);

  // Dismiss the update notification
  const handleDismissUpdate = useCallback(() => {
    setShowUpdateNotification(false);
  }, []);

  // Make handleUpdateAvailable available to index.js via window object
  useEffect(() => {
    window.onServiceWorkerUpdate = handleUpdateAvailable;
    return () => {
      delete window.onServiceWorkerUpdate;
    };
  }, [handleUpdateAvailable]);

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  // Protected route wrapper that checks for password change requirement
  // Three layouts, not two. `main-content` carries a 250px left margin for the
  // sidebar, so it is wrong when the nav is hidden; a bare wrapper has no
  // padding at all, which is right for the full-bleed TV pages and wrong for a
  // blocking screen somebody has to read. `blocking-content` is the middle
  // case: padded and centred, no sidebar gutter.
  const isBlockingScreen = isAuthenticated
    && !isStandaloneRoute
    && (requirePasswordChange || requireRulesAcknowledgment);

  let contentClassName = '';
  if (isBlockingScreen) {
    contentClassName = 'blocking-content';
  } else if (isAuthenticated && !isStandaloneRoute) {
    contentClassName = 'main-content';
  } else if (!isStandaloneRoute) {
    // Signed-out pages had no class at all, which left them as an unstyled child
    // of a flex row: the box shrank to its content instead of filling the window,
    // so a full-bleed public page sat in a narrow column with the body background
    // showing down both sides.
    contentClassName = 'public-content';
  }

  // `allowUnacknowledgedRules` exempts the rules screen itself. Without it the
  // redirect below would send that route back to itself forever.
  const ProtectedRoute = ({ children, allowUnacknowledgedRules }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (requirePasswordChange) {
      return <Navigate to="/change-password" />;
    }
    // Ordered after the password change deliberately: someone on a temporary
    // password should set a real one before signing anything.
    if (requireRulesAcknowledgment && !allowUnacknowledgedRules) {
      return <Navigate to="/acknowledge-rules" />;
    }
    return children;
  };

  return (
    <ToastProvider>
      <Router>
        <LocationWatcher onChange={setCurrentPath} />
        <div className="App">
          {isAuthenticated && !requirePasswordChange && !requireRulesAcknowledgment && !isStandaloneRoute && <Navigation onLogout={handleLogout} />}

          {/* Global toast notifications */}
          {!isStandaloneRoute && <Toast />}

          {/* Scan notification modal - shown globally when QR is scanned */}
          {!isStandaloneRoute && (
            <ScanNotification
              notification={scanNotification}
              onDismiss={dismissScanNotification}
            />
          )}

          {/* Update notification toast - shown when PWA update is available */}
          {!isStandaloneRoute && (
            <UpdateNotification
              show={showUpdateNotification}
              onUpdate={handleUpdateNow}
              onDismiss={handleDismissUpdate}
            />
          )}

          <div className={contentClassName}>
          {/* TV screens say they are offline themselves, in a way built for a room. */}
          {!isStandaloneRoute && <ConnectionBanner />}
          <Routes>
            <Route 
              path="/login" 
              element={
                isAuthenticated ? 
                  (requirePasswordChange
                    ? <Navigate to="/change-password" />
                    : requireRulesAcknowledgment
                      ? <Navigate to="/acknowledge-rules" />
                      : <Navigate to="/my-membership" />) : 
                  <Login onLogin={handleLogin} />
              } 
            />
            
            <Route 
              path="/change-password" 
              element={
                isAuthenticated ? 
                  <ChangePassword isRequired={requirePasswordChange} onPasswordChanged={handlePasswordChanged} /> : 
                  <Navigate to="/login" />
              } 
            />
            
            {/* Access Denied page */}
            <Route 
              path="/access-denied" 
              element={<AccessDenied />} 
            />
            
            <Route 
              path="/users" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <Users />
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            {/* The merge review is its own destination, not a modal over the
                user list. It is a screen's worth of decision with no undo, and
                as a modal its "back" was a close button pretending to be
                navigation - the browser back button left the page entirely. */}
            <Route
              path="/users/merge"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <MergeReview />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            
            <Route 
              path="/users/:userId" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <ManageUser />
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/developer/devices"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <Devices />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/developer/badges"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <BadgeManagement />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* My Membership tabs - Home/Standing/Assigned/Profile, switched via
                MyAccountBottomNav rather than separate page components,
                mirroring how MachineIssues.js handles Issues/Queue/Triage.
                The last three paths are aliases from the earlier four-tab
                split: /credit resolves to Home, /badges to Standing, and /tech
                to Profile, so existing links and notification deep links keep
                working. */}
            <Route
              path="/my-membership"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/standing"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/assigned"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/profile"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/credit"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/badges"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-membership/tech"
              element={
                <ProtectedRoute>
                  <MyMembership />
                </ProtectedRoute>
              }
            />

            
            
            <Route 
              path="/square-integration" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <SquareIntegration />
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/square-catalog-config" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <SquareCatalogConfig />
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            
            
            
            <Route 
              path="/square-sync-history" 
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <SquareSyncHistory />
                  </RoleGuard>
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/developer"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <DeveloperTools />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            <Route
              path="/developer/design-system"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <DesignSystemReference />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            
            {/* Public routes - no auth required */}
            <Route 
              path="/verify/:qrCode" 
              element={<VerifyMember />} 
            />
            
            <Route 
              path="/apply" 
              element={<ApplyMembership />} 
            />
            
            <Route 
              path="/forgot-password" 
              element={isAuthenticated ? <Navigate to="/my-membership" /> : <ForgotPassword />} 
            />
            
            <Route 
              path="/reset-password" 
              element={isAuthenticated ? <Navigate to="/my-membership" /> : <ResetPassword />} 
            />
            
            <Route 
              path="/claim-account" 
              element={isAuthenticated ? <Navigate to="/my-membership" /> : <ClaimAccount />} 
            />
            
            <Route
              path="/developer/board-games"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <BoardGameLibrary />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />
            {/* Moved into Developer Tools. */}
            <Route path="/board-games" element={<Navigate to="/developer/board-games" replace />} />

            <Route
              path="/rules"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <RulesCompliance />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Authenticated onboarding routes */}
            <Route 
              path="/acknowledge-rules"
              element={
                <ProtectedRoute allowUnacknowledgedRules>
                  <AcknowledgeRules
                    isRequired={requireRulesAcknowledgment}
                    onAcknowledged={handleRulesAcknowledged}
                  />
                </ProtectedRoute>
              }
            />
            

            <Route 
              path="/events" 
              element={
                <ProtectedRoute>
                  <EventsHub />
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="/pingolf"
              element={
                <ProtectedRoute>
                  <PinGolfEvents />
                </ProtectedRoute>
              }
            />

            {/* Player-facing PinGolf League landing tab: status, next event, leaderboard */}
            <Route
              path="/pingolf-season"
              element={
                <ProtectedRoute>
                  <PinGolfLeagueOverview />
                </ProtectedRoute>
              }
            />

            <Route 
              path="/events/:eventId" 
              element={
                <ProtectedRoute>
                  <EventDetails />
                </ProtectedRoute>
              } 
            />

            <Route
              path="/events/:eventId/manage"
              element={
                <ProtectedRoute>
                  <EventManage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/events/:eventId/host-controls"
              element={
                <ProtectedRoute>
                  <PingolfHostControls />
                </ProtectedRoute>
              }
            />

            <Route
              path="/events/:eventId/pingolf-cards/:cardId"
              element={
                <ProtectedRoute>
                  <PinGolfCard />
                </ProtectedRoute>
              }
            />

            <Route 
              path="/help" 
              element={
                <ProtectedRoute>
                  <KnowledgeBaseHome />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/help/category/:slug" 
              element={
                <ProtectedRoute>
                  <KnowledgeBaseCategory />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/help/article/:slug" 
              element={
                <ProtectedRoute>
                  <KnowledgeBaseArticle />
                </ProtectedRoute>
              } 
            />
            
            {/* Public RSVP routes */}
            <Route 
              path="/events/:eventId/rsvp" 
              element={<EventRSVP />} 
            />
            
            <Route 
              path="/events/:eventId/rsvp-confirmation" 
              element={<RSVPConfirmation />} 
            />
            
            {/* Transformers Pinball Championship routes */}
            <Route
              path="/competitions"
              element={
                <ProtectedRoute>
                  <FeatureRoleGuard featureKey="transformers_competition">
                    <CompetitionAdmin />
                  </FeatureRoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Member-facing "how's the season going" view - any logged-in member, not just admins */}
            <Route
              path="/competitions/broadcast"
              element={
                <ProtectedRoute>
                  <CompetitionBroadcast />
                </ProtectedRoute>
              }
            />

            {/* Public, no-auth TV dashboard - meant to run continuously on a screen in the club */}
            <Route
              path="/competitions/:id/tv"
              element={<TransformersTvDashboard />}
            />

            <Route
              path="/my-competition"
              element={
                <ProtectedRoute>
                  <MyCompetition />
                </ProtectedRoute>
              }
            />
            
            {/* PinGolf League (season play) */}
            <Route
              path="/pingolf-season/admin"
              element={
                <ProtectedRoute>
                  <FeatureRoleGuard featureKey="penthouse_league">
                    <PinGolfLeagueAdmin />
                  </FeatureRoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pingolf-season/admin/new"
              element={
                <ProtectedRoute>
                  <FeatureRoleGuard featureKey="penthouse_league">
                    <PinGolfLeagueSeasonCreate />
                  </FeatureRoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pingolf-season/admin/:seasonId"
              element={
                <ProtectedRoute>
                  <FeatureRoleGuard featureKey="penthouse_league">
                    <PinGolfLeagueAdminSeason />
                  </FeatureRoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Public, no-auth TV dashboard */}
            <Route path="/pingolf-season/:seasonId/tv" element={<PinGolfLeagueTv />} />

            {/* The club leaderboard screen. No auth by necessity: it runs in
                signage software that can never sign in. */}
            <Route path="/scorekeeping/tv" element={<ScorekeepingTv />} />

            {/* The club's events screen: this week's events on a TV. Public for the
                same reason. A static path, so it outranks /events/:eventId. */}
            <Route path="/events/tv" element={<EventsTv />} />

            {/* The hallway sign by the front door: guest pass, membership, tonight's
                hosts. Public for the same reason. */}
            <Route path="/hallway/tv" element={<HallwayTv />} />

            {/* Score capture. Public throughout: anyone at the club can put up a
                score, and the signup at the end is how a guest becomes a member. */}
            <Route path="/scorekeeping/machines/:machineId" element={<MachineScorePage />} />
            <Route path="/scorekeeping/machines/:machineId/submit" element={<SubmitScore />} />
            <Route path="/scorekeeping/signup" element={<ScoreSignup />} />

            {/* The monthly competition. Public, with more once we know who you
                are: the club screen and a guest who just played both need it. */}
            <Route path="/scorekeeping/leaderboard" element={<Leaderboard />} />

            {/* Running the competition - admin only, enforced by the API */}
            <Route
              path="/scorekeeping/admin"
              element={
                <ProtectedRoute>
                  <ScorekeepingAdmin />
                </ProtectedRoute>
              }
            />

            {/* Public season page */}
            <Route path="/pingolf-season/:seasonId" element={<PinGolfLeagueSeason />} />

            {/* Event series management - admin only */}
            <Route 
              path="/event-series" 
              element={
                <ProtectedRoute>
                  <EventSeriesManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* Every night a series has generated - admin only */}
            <Route
              path="/event-series/:id/dates"
              element={
                <ProtectedRoute>
                  <SeriesDates />
                </ProtectedRoute>
              }
            />

            {/* Host volunteer requests - admin only */}
            <Route 
              path="/host-volunteer-requests" 
              element={
                <ProtectedRoute>
                  <HostVolunteerRequests />
                </ProtectedRoute>
              } 
            />
            
            {/* Automation settings - admin only */}
            <Route 
              path="/automation-settings" 
              element={
                <ProtectedRoute>
                  <AutomationSettings />
                </ProtectedRoute>
              } 
            />
            
            {/* Machine management - admin only */}
            <Route 
              path="/machines" 
              element={
                <ProtectedRoute>
                  <MachineManagement />
                </ProtectedRoute>
              } 
            />
            
            {/* Machine Maintenance - one landing screen for members and
                Maintenance Organizers alike, role-gated inline like
                Issues/Q/Triage (must precede /machines/:machineId). */}
            <Route
              path="/machines/maintenance"
              element={
                <ProtectedRoute>
                  <MachineMaintenanceList />
                </ProtectedRoute>
              }
            />
            {/* The Organizer Dashboard was merged into the list above
                (2026-08-17); kept as a redirect so existing bookmarks and the
                Checklist editor's back button don't 404. */}
            <Route
              path="/machines/maintenance/organizer"
              element={<Navigate to="/machines/maintenance" replace />}
            />
            <Route
              path="/machines/maintenance/template"
              element={
                <ProtectedRoute>
                  <FeatureRoleGuard featureKey="machine_maintenance">
                    <MaintenanceTemplateEditor />
                  </FeatureRoleGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/machines/maintenance/:machineId"
              element={
                <ProtectedRoute>
                  <MaintenanceRecordDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/machines/:machineId"
              element={
                <ProtectedRoute>
                  <MachineDetail />
                </ProtectedRoute>
              }
            />

            {/* Machine Issues tabs - Issues/Triage, merged into the Machines nav
                item and switched via MachineBottomNav rather than a separate
                sidebar entry. */}
            <Route
              path="/machines/issues"
              element={
                <ProtectedRoute>
                  <MachineIssues />
                </ProtectedRoute>
              }
            />
            <Route
              path="/machines/triage"
              element={
                <ProtectedRoute>
                  <MachineIssues />
                </ProtectedRoute>
              }
            />

            {/* Old bare list URL - redirect to its new home under /machines */}
            <Route path="/machine-issues" element={<Navigate to="/machines/issues" replace />} />

            <Route
              path="/machine-issues/:issueId"
              element={
                <ProtectedRoute>
                  <MachineIssueDetail />
                </ProtectedRoute>
              } 
            />
            
            {/* Public machine page - no auth required */}
            <Route 
              path="/machines/:machineId/public" 
              element={<MachinePublic />} 
            />
            
            {/* Task Management - member-facing routes */}
            <Route
              path="/tasks"
              element={<ProtectedRoute><TaskList /></ProtectedRoute>}
            />
            <Route
              path="/tasks/suggest"
              element={<ProtectedRoute><TaskSuggest /></ProtectedRoute>}
            />
            <Route
              path="/tasks/my-suggestions"
              element={<ProtectedRoute><MySuggestions /></ProtectedRoute>}
            />

            {/* Task Management - Task Delegate / Admin routes (must precede /tasks/:id) */}
            <Route
              path="/tasks/new"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskForm /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/templates"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskTemplateLibrary /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/templates/new"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskForm /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/templates/:id/edit"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskForm /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/walkthrough"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><MaintenanceWalkthrough /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/needs-attention"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskNeedsAttention /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/leaderboard"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskLeaderboard /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/reporting"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskReporting /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/suggestions/:id/review"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskSuggestionReview /></FeatureRoleGuard></ProtectedRoute>}
            />

            {/* Task Management - task instance routes */}
            <Route
              path="/tasks/:id/edit"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskForm /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/:id/confirm-bounty"
              element={<ProtectedRoute><FeatureRoleGuard featureKey="task_management"><TaskBountyConfirm /></FeatureRoleGuard></ProtectedRoute>}
            />
            <Route
              path="/tasks/:id/force-undo"
              element={<ProtectedRoute><TaskForceUndo /></ProtectedRoute>}
            />
            <Route
              path="/tasks/:id"
              element={<ProtectedRoute><TaskDetail /></ProtectedRoute>}
            />

            <Route
              path="/"
              element={
                <Navigate to={
                  isAuthenticated ? 
                    (requirePasswordChange
                      ? "/change-password"
                      : requireRulesAcknowledgment ? "/acknowledge-rules" : "/my-membership") : 
                    "/login"
                } />
              } 
            />
            
            <Route 
              path="*" 
              element={<NotFound isAuthenticated={isAuthenticated} />} 
            />
          </Routes>
        </div>
      </div>
    </Router>
    </ToastProvider>
  );
}

export default App;
